/**
 * Organization-level access control for Better Auth organization plugin.
 *
 * Defines custom permissions and roles scoped to organizations.
 * This is separate from the admin plugin's global access control
 * (access-control.ts) which handles the platform role axis (superuser, user).
 *
 * Org roles (highest first): owner > admin > secretary > guest
 * Platform role: superuser bypasses everything.
 */
import { createAccessControl } from "better-auth/plugins/access";
import {
	adminAc,
	defaultStatements,
	memberAc,
	ownerAc,
} from "better-auth/plugins/organization/access";

const statements = {
	...defaultStatements,
	events: ["create", "read", "update", "delete"],
	config: ["read", "update"],
	conversations: ["read", "manage"],
	documents: ["read", "create", "update", "delete"],
} as const;

const ac = createAccessControl(statements);

const owner = ac.newRole({
	...ownerAc.statements,
	events: ["create", "read", "update", "delete"],
	config: ["read", "update"],
	conversations: ["read", "manage"],
	documents: ["read", "create", "update", "delete"],
});

const admin = ac.newRole({
	...adminAc.statements,
	events: ["create", "read", "update", "delete"],
	config: ["read", "update"],
	conversations: ["read", "manage"],
	documents: ["read", "create", "update", "delete"],
});

// Secretary mirrors admin's operational reach over org resources (the day-to-day
// operator role) — same statements as admin so secretary invites are accepted
// and can act on events/config/conversations/documents.
const secretary = ac.newRole({
	...adminAc.statements,
	events: ["create", "read", "update", "delete"],
	config: ["read", "update"],
	conversations: ["read", "manage"],
	documents: ["read", "create", "update", "delete"],
});

// Guest is a read-only observer: no org-management authority (memberAc base),
// read-only over org resources, with conversation read only.
const guest = ac.newRole({
	...memberAc.statements,
	events: ["read"],
	config: ["read"],
	conversations: ["read"],
	documents: ["read"],
});

export const orgAccessControl = {
	ac,
	roles: { owner, admin, secretary, guest },
} as const;
